import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Stack } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useAuth } from '../contexts/AuthContext';

const FONT = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';
const REDUCE = { '@media (prefers-reduced-motion: reduce)': { transition: 'none !important', transform: 'none !important', animation: 'none !important' } };

// Brand accent tokens (roles all live in the business workspace).
const ACCENT = '#EA580C';
const ACCENT_RING = 'rgba(255,102,0,0.55)';
const ACCENT_GLOW = 'rgba(234,88,12,0.26)';
const ACCENT_SOFT = '#FFF3EC';

/** Glassmorphism role picker shown when a user holds more than one role.
 *  Picking one scopes the session's permissions to that role. Matches the
 *  panel selector's visual language. */
export default function RoleChooser() {
  const navigate = useNavigate();
  const { roles = [], selectRole, profile, logout } = useAuth();
  const [selected, setSelected] = useState(roles[0]?.id || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const proceed = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setError('');
    try {
      await selectRole(selected);
      navigate('/dashboard', { replace: true });
    } catch (e) {
      setError('Could not apply that role. Please try again.');
      setBusy(false);
    }
  };

  const firstName = profile?.name ? profile.name.split(' ')[0] : '';

  return (
    <Box sx={{ position: 'relative', minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3 }, fontFamily: FONT, overflow: 'hidden', background: 'linear-gradient(160deg,#F7F8FC 0%,#EEF0F7 100%)' }}>
      {/* ambient light sources (static) */}
      <Box aria-hidden sx={{ position: 'absolute', top: '-12%', left: '-6%', width: 440, height: 440, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.18), transparent 66%)', filter: 'blur(30px)' }} />
      <Box aria-hidden sx={{ position: 'absolute', bottom: '-14%', right: '-8%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,138,61,0.22), transparent 66%)', filter: 'blur(30px)' }} />

      {/* glass shell */}
      <Box sx={{
        position: 'relative', width: '100%', maxWidth: 460, p: { xs: 2.5, sm: 3.5 }, borderRadius: '28px',
        background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(22px)', WebkitBackdropFilter: 'blur(22px)',
        border: '1px solid rgba(255,255,255,0.7)', boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 30px 80px rgba(15,23,42,0.14)',
      }}>
        {/* header */}
        <Stack alignItems="center" spacing={1.25} sx={{ mb: 3, textAlign: 'center' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', display: 'grid', placeItems: 'center', background: `linear-gradient(135deg,#FF8A3D,${ACCENT})`, boxShadow: `0 8px 20px ${ACCENT_GLOW}` }}>
            <BadgeOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: { xs: '1.4rem', sm: '1.6rem' }, letterSpacing: '-0.02em', color: '#0F172A' }}>
            Choose a role{firstName ? `, ${firstName}` : ''}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: '#64748B', maxWidth: 380 }}>
            You have more than one role. Pick the one to work as — switch anytime from the account menu.
          </Typography>
        </Stack>

        {/* role options */}
        <Box role="radiogroup" aria-label="Select a role" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {roles.map((r) => {
            const active = selected === r.id;
            return (
              <Box
                key={r.id}
                role="radio"
                aria-checked={active}
                tabIndex={0}
                onClick={() => setSelected(r.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(r.id); } }}
                sx={{
                  position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5,
                  p: 1.75, borderRadius: '16px',
                  background: active ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.62)',
                  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                  border: `1.5px solid ${active ? ACCENT_RING : 'rgba(255,255,255,0.7)'}`,
                  boxShadow: active ? `0 12px 28px ${ACCENT_GLOW}` : '0 6px 16px rgba(15,23,42,0.05)',
                  transition: 'transform .18s cubic-bezier(.16,.84,.44,1), box-shadow .18s, border-color .18s, background .18s',
                  '&:hover': { transform: 'translateY(-2px)', borderColor: ACCENT_RING },
                  '&:focus-visible': { outline: `3px solid ${ACCENT_RING}`, outlineOffset: 2 },
                  ...REDUCE,
                }}
              >
                {/* radio indicator */}
                <Box aria-hidden sx={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', display: 'grid', placeItems: 'center', border: `2px solid ${active ? ACCENT : '#CBD5E1'}`, transition: 'border-color .18s', ...REDUCE }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ACCENT, transform: active ? 'scale(1)' : 'scale(0)', transition: 'transform .18s', ...REDUCE }} />
                </Box>
                {/* icon tile */}
                <Box sx={{ flexShrink: 0, width: 38, height: 38, borderRadius: '11px', display: 'grid', placeItems: 'center', bgcolor: active ? ACCENT_SOFT : 'rgba(100,116,139,0.10)', color: active ? '#C2410C' : '#64748B', transition: 'all .18s', ...REDUCE }}>
                  <BadgeOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ flex: 1, minWidth: 0, fontFamily: FONT, fontWeight: 700, fontSize: '0.96rem', color: '#0F172A' }} noWrap>{r.name}</Typography>
                {active && <CheckRoundedIcon sx={{ color: ACCENT, fontSize: 20, flexShrink: 0 }} />}
              </Box>
            );
          })}
        </Box>

        {error && (
          <Typography role="alert" sx={{ fontFamily: FONT, color: '#DC2626', fontSize: '0.85rem', mt: 1.75, textAlign: 'center' }}>{error}</Typography>
        )}

        {/* primary action */}
        <Button
          fullWidth
          onClick={proceed}
          disabled={!selected || busy}
          endIcon={!busy && <ArrowForwardRoundedIcon />}
          sx={{
            mt: 3, py: 1.3, textTransform: 'none', fontFamily: FONT, fontWeight: 800, fontSize: '0.95rem',
            borderRadius: '14px', color: '#fff', background: `linear-gradient(135deg,#FF8A3D,${ACCENT})`,
            boxShadow: `0 12px 26px ${ACCENT_GLOW}`,
            '&:hover': { background: `linear-gradient(135deg,#FF7A22,#D9500A)`, boxShadow: `0 16px 32px ${ACCENT_GLOW}` },
            '&.Mui-disabled': { background: 'rgba(15,23,42,0.10)', color: 'rgba(15,23,42,0.4)', boxShadow: 'none' },
            ...REDUCE,
          }}
        >
          {busy ? 'Applying…' : 'Continue'}
        </Button>

        {/* footer */}
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Button onClick={logout} startIcon={<LogoutRoundedIcon sx={{ fontSize: 17 }} />}
            sx={{ textTransform: 'none', fontFamily: FONT, fontWeight: 600, fontSize: '0.82rem', color: '#64748B', borderRadius: '10px', '&:hover': { color: '#0F172A', bgcolor: 'rgba(15,23,42,0.04)' }, ...REDUCE }}>
            Not you? Sign out
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
